import { PRODUCT_FEATURES, PRODUCT_FEATURE_STATUSES } from "core/consts";
import {
  CareseekerConfig,
  OnpremiseConfig,
  ProductFeature,
  ProductFeatureDependency,
  ProductFeatureSubFeature,
  ProductFeatureType,
} from "core/types";
import { getUnixTime } from "date-fns";

export const getProductFeature = (
  productFeatures: ProductFeature[] | undefined,
  feature: ProductFeatureType,
): ProductFeature | null => {
  return (
    productFeatures?.find?.(
      (productFeature) => feature === productFeature.feature,
    ) ?? null
  );
};

export const getProductFeatureActive = (
  productFeatures: ProductFeature[] | undefined,
  feature: ProductFeatureType,
): boolean => {
  const productFeature = getProductFeature(productFeatures, feature);

  if (!productFeature) return false;

  if (productFeature.status === PRODUCT_FEATURE_STATUSES.TRIAL) {
    if (!productFeature.start_date || !productFeature.expiration_date) {
      return false;
    }

    return (
      productFeature.start_date <= getUnixTime(new Date()) &&
      productFeature.expiration_date > getUnixTime(new Date())
    );
  }

  return productFeature.status === PRODUCT_FEATURE_STATUSES.ACTIVE;
};

export const getProductFeatureDependencies = (
  careseekerConfig: CareseekerConfig | undefined,
  feature: ProductFeatureType,
): ProductFeatureDependency[] => {
  if (!careseekerConfig) return [];

  const configMap: Record<ProductFeatureType, ProductFeatureDependency[]> = {
    [PRODUCT_FEATURES.ADMISSIONS]: [],
    [PRODUCT_FEATURES.AI_CHAT]: [],
    [PRODUCT_FEATURES.ANALYTICS]: [],
    [PRODUCT_FEATURES.DISCHARGE_LETTER]: [],
    [PRODUCT_FEATURES.DOCS_AND_EXTRACT]: [],
    [PRODUCT_FEATURES.DTA]: [
      {
        configPath: "careseeker.config.activate_dta",
        description:
          "DTA must be activated in configuration before this feature can run.",
        isActive: !!careseekerConfig.activate_dta,
        label: "activate_dta",
      },
    ],
    [PRODUCT_FEATURES.PREDICT]: [],
    [PRODUCT_FEATURES.TRANSITIONAL_CARE]: [],
    [PRODUCT_FEATURES.VOICE]: [],
  };

  return configMap[feature] || [];
};

export const getProductFeatureSubFeatures = (
  onpremiseConfig: OnpremiseConfig | undefined,
  feature: ProductFeatureType,
): ProductFeatureSubFeature[] => {
  const isSpecializedModelEnabled =
    onpremiseConfig?.config?.predict_config?.specialized_model_config
      ?.specialized_model_enabled;

  const configMap: Record<ProductFeatureType, ProductFeatureSubFeature[]> = {
    [PRODUCT_FEATURES.ADMISSIONS]: [],
    [PRODUCT_FEATURES.AI_CHAT]: [],
    [PRODUCT_FEATURES.ANALYTICS]: [],
    [PRODUCT_FEATURES.DISCHARGE_LETTER]: [],
    [PRODUCT_FEATURES.DOCS_AND_EXTRACT]: [],
    [PRODUCT_FEATURES.DTA]: [],
    [PRODUCT_FEATURES.PREDICT]: [
      {
        description:
          "Uses a fine-tuned model specific to this customer's data.",
        isActive: !!isSpecializedModelEnabled,
        key: "onpremiseConfig.config.predict_config.specialized_model_config.specialized_model",
        label: "Specialised model",
      },
    ],
    [PRODUCT_FEATURES.TRANSITIONAL_CARE]: [],
    [PRODUCT_FEATURES.VOICE]: [],
  };

  return configMap[feature] || [];
};
